"use client"

import React, { useState, useEffect, useRef, useContext } from "react"
import ApexCharts from "apexcharts"
import { styled, useTheme, ThemeProvider, createTheme } from "@mui/material/styles"
import { Container, Grid, Paper, Typography, Button, CssBaseline, useMediaQuery, Box, Card, CardContent } from "@mui/material"
import { LightMode, DarkMode, DirectionsCar, Speed, AccessTime, Alarm } from "@mui/icons-material"

// Interfaces para tipagem
interface Device {
  id: number
  name: string
  status: string
  lastUpdate: string
  positionId: number
  groupId: number
  phone: string
  model: string
  contact: string
  category: string
  disabled: boolean
  attributes: {
    [key: string]: any
  }
}

interface Position {
  id: number
  deviceId: number
  protocol: string
  serverTime: string
  deviceTime: string
  fixTime: string
  outdated: boolean
  valid: boolean
  latitude: number
  longitude: number
  altitude: number
  speed: number
  course: number
  address: string
  accuracy: number
  network: any
  attributes: {
    [key: string]: any
  }
}

interface StateCountsType {
  [key: string]: string[]
}

// Contexto para o tema
const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
})

// Contexto para traduções
const TranslationContext = React.createContext({
  t: (key: string) => key,
  changeLanguage: (lang: string) => {},
})

// Styled components atualizados com base no projeto original
const Root = styled("div")(({ theme }) => ({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  transition: "all 0.3s ease",
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
}))

const Header = styled("header")(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#2c3e50",
  color: "#ecf0f1",
  boxShadow: theme.shadows[4],
  position: "sticky",
  top: 0,
  zIndex: 1000,
}))

const Content = styled(Container)(({ theme }) => ({
  maxWidth: "100%",
  margin: "0 auto",
  padding: theme.spacing(3),
  flex: 1,
  overflow: "auto",
  backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#f5f7fa",
}))

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 20px rgba(0, 0, 0, 0.5)"
    : "0 4px 20px rgba(0, 0, 0, 0.1)",
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
  transition: "all 0.3s ease",
  height: "100%",
  overflow: "hidden",
  border: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
}))

const ChartWrapper = styled("div")(({ theme }) => ({
  height: 350,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 0.7)" : "rgba(245, 247, 250, 0.8)",
  borderRadius: "8px",
  padding: "10px",
}))

const StatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  backgroundColor: theme.palette.mode === "dark" ? "#2c3e50" : "#3498db",
  color: "white",
  borderRadius: "12px",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 20px rgba(0, 0, 0, 0.5)"
    : "0 4px 20px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 8px 25px rgba(0, 0, 0, 0.6)"
      : "0 8px 25px rgba(0, 0, 0, 0.15)",
  },
}))

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#f5f7fa",
}))

const LoaderAnimation = styled("div")(({ theme }) => ({
  position: "relative",
  width: 80,
  height: 80,
  "& div": {
    position: "absolute",
    width: 64,
    height: 64,
    margin: 8,
    border: `8px solid ${theme.palette.primary.main}`,
    borderRadius: "50%",
    animation: "loader-animation 1.2s linear infinite",
    borderColor: `${theme.palette.primary.main} transparent transparent transparent`,
  },
  "@keyframes loader-animation": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}))

// Sistema de tradução baseado no projeto original
const translations = {
  en: {
    "sharedAlarms": "Alarms",
    "positionMotion": "Motion Status",
    "deviceLastUpdate": "Last Update",
    "positionOdometer": "Odometer",
    "positionHours": "Engine Hours",
    "reportTodayActivity": "Today's Activity",
    "sharedTotal": "Total",
    "sharedKm": "km",
    "sharedHours": "hours",
    "deviceStatusOnline": "Online",
    "deviceHours12": "12 hours",
    "deviceHours24": "24 hours",
    "deviceHours36": "36 hours",
    "deviceHours48": "48 hours",
    "deviceHoursMore48": "+48 hours",
    "eventDeviceMoving": "Moving",
    "eventDeviceStopped": "Stopped",
    "eventDeviceInactive": "Inactive",
    "reportDashboard": "Dashboard",
    "themeLightMode": "Light Mode",
    "themeDarkMode": "Dark Mode",
    "errorFetchingDevices": "Error fetching devices",
    "errorFetchingPositions": "Error fetching positions",
    "errorLoadingData": "Error loading data",
    "sharedRetry": "Retry",
    "sharedLoading": "Loading",
  },
  pt: {
    "sharedAlarms": "Alarmes",
    "positionMotion": "Status de Movimento",
    "deviceLastUpdate": "Última Atualização",
    "positionOdometer": "Odômetro",
    "positionHours": "Horas de Motor",
    "reportTodayActivity": "Atividade de Hoje",
    "sharedTotal": "Total",
    "sharedKm": "km",
    "sharedHours": "horas",
    "deviceStatusOnline": "Online",
    "deviceHours12": "12 horas",
    "deviceHours24": "24 horas",
    "deviceHours36": "36 horas",
    "deviceHours48": "48 horas",
    "deviceHoursMore48": "+48 horas",
    "eventDeviceMoving": "Em Movimento",
    "eventDeviceStopped": "Parado",
    "eventDeviceInactive": "Inativo",
    "reportDashboard": "Painel de Controle",
    "themeLightMode": "Modo Claro",
    "themeDarkMode": "Modo Escuro",
    "errorFetchingDevices": "Erro ao buscar dispositivos",
    "errorFetchingPositions": "Erro ao buscar posições",
    "errorLoadingData": "Erro ao carregar dados",
    "sharedRetry": "Tentar Novamente",
    "sharedLoading": "Carregando",
  }
}

// Provedor de tradução
const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState("pt")

  const t = (key: string) => {
    return translations[language][key] || key
  }

  const changeLanguage = (lang: string) => {
    setLanguage(lang)
  }

  return (
    <TranslationContext.Provider value={{ t, changeLanguage }}>
      {children}
    </TranslationContext.Provider>
  )
}

// Hook personalizado para usar traduções
const useTranslation = () => {
  const context = useContext(TranslationContext)
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider")
  }
  return context
}

// Componente principal do Dashboard
const Dashboard = () => {
  const theme = useTheme()
  const colorMode = useContext(ColorModeContext)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const { t } = useTranslation()
  const isDark = theme.palette.mode === "dark"

  // Estados para armazenar dados
  const [devices, setDevices] = useState<Device[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState({
    totalDevices: 0,
    movingDevices: 0,
    stoppedDevices: 0,
    totalDistance: 0,
    totalHours: 0
  })

  // Refs para os gráficos
  const alarmChartRef = useRef(null)
  const ignitionChartRef = useRef(null)
  const statesChartRef = useRef(null)
  const odometerChartRef = useRef(null)
  const hoursChartRef = useRef(null)
  const todayChartRef = useRef(null)

  // Cores para os gráficos baseadas no tema
  const primary = theme.palette.primary.main
  const secondary = theme.palette.secondary.main
  const textColor = theme.palette.text.primary

  // Configuração global para cores do ApexCharts
  useEffect(() => {
    if (window.ApexCharts) {
      window.ApexCharts.colors = [
        secondary,
        primary,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        theme.palette.success.main,
        "#546E7A",
        "#D4526E",
        "#8D5B4C",
        "#F86624",
        "#A5978B",
        "#4ECDC4",
        "#C7F464",
        "#81D4FA",
        "#FD6A02",
        "#7B1FA2",
        "#FFC300",
        "#FF5733",
        "#C70039",
        "#900C3F",
      ]
    }
  }, [theme, primary, secondary])

  // Função para buscar dados de dispositivos e posições
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar dados da API Traccar
        const sessionResponse = await fetch("/api/session")
        if (!sessionResponse.ok) {
          throw new Error("Not authenticated")
        }

        // Buscar dispositivos
        const devicesResponse = await fetch("/api/devices")
        if (!devicesResponse.ok) {
          throw new Error(t("errorFetchingDevices"))
        }
        const devicesData = await devicesResponse.json()

        // Buscar posições
        const positionsResponse = await fetch("/api/positions")
        if (!positionsResponse.ok) {
          throw new Error(t("errorFetchingPositions"))
        }
        const positionsData = await positionsResponse.json()

        setDevices(devicesData)
        setPositions(positionsData)

        // Calcular estatísticas
        calculateStats(devicesData, positionsData)
        setLoading(false)
      } catch (err) {
        console.error("Erro ao carregar dados:", err)
        setError(err.message || t("errorLoadingData"))
        setLoading(false)
      }
    }

    const calculateStats = (devices: Device[], positions: Position[]) => {
      let moving = 0
      let stopped = 0
      let totalDistance = 0
      let totalHours = 0

      positions.forEach(position => {
        if (position.attributes.motion) {
          moving++
        } else {
          stopped++
        }

        if (position.attributes.odometer) {
          totalDistance += position.attributes.odometer
        }

        if (position.attributes.hours) {
          totalHours += position.attributes.hours
        }
      })

      setStats({
        totalDevices: devices.length,
        movingDevices: moving,
        stoppedDevices: stopped,
        totalDistance: Math.round(totalDistance / 1000), // Convert to km
        totalHours: Math.round(totalHours / 3600) // Convert to hours
      })
    }

    fetchData()

    // Configurar atualização automática a cada 30 segundos
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [t])

  // Configuração base para todos os gráficos
  const getBaseChartOptions = () => ({
    chart: {
      foreColor: textColor,
      background: "transparent",
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    theme: {
      mode: isDark ? "dark" : "light",
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      style: {
        fontSize: "14px",
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      },
    },
    legend: {
      labels: {
        colors: textColor,
        useSeriesColors: false,
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      },
    },
    title: {
      style: {
        color: textColor,
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        fontSize: "18px",
        fontWeight: "bold",
      },
    },
    xaxis: {
      labels: {
        style: {
          colors: textColor,
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: textColor,
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        },
      },
    },
  })

  // GRÁFICO DE ALARMES
  useEffect(() => {
    if (!alarmChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Calcular estatísticas de alarmes
    const stateCounts: StateCountsType = {}
    stateCounts["Normal"] = []

    positions.forEach((position) => {
      if (position.attributes && position.attributes.alarm) {
        const alarmType = position.attributes.alarm
        if (!stateCounts[alarmType]) {
          stateCounts[alarmType] = []
        }
        stateCounts[alarmType].push(position.deviceId)
      } else {
        stateCounts["Normal"].push(position.deviceId)
      }
    })

    const stateKeys = Object.keys(stateCounts)
    const stateValues = stateKeys.map((key) => (stateCounts[key] || []).length)

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      series: stateValues,
      title: {
        text: t("sharedAlarms"),
        align: "center",
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: isMobile ? "bottom" : "right",
        fontSize: "14px",
        fontWeight: 600,
        markers: {
          width: 14,
          height: 14,
          radius: 7,
          strokeWidth: 0,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      labels: stateKeys.map((key) => t(`alarm${key}`) || key),
      plotOptions: {
        pie: {
          donut: {
            size: "55%",
            background: "transparent",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                color: textColor,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 600,
                color: textColor,
                offsetY: 16,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: t("sharedTotal"),
                fontSize: "22px",
                fontWeight: 700,
                color: textColor,
                formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
        },
      },
      colors: ["#4CAF50", "#F44336", "#FFC107", "#2196F3", "#9C27B0", "#FF9800"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"],
      },
    }

    const chart = new ApexCharts(alarmChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isMobile, textColor, isDark])

  // GRÁFICO DE IGNIÇÃO (Movimento)
  useEffect(() => {
    if (!ignitionChartRef.current || loading || positions.length === 0) return

    // Calcular estatísticas de movimento
    const moving = positions.filter(p => p.attributes.motion).length
    const stopped = positions.filter(p => !p.attributes.motion && p.speed === 0).length
    const idle = positions.filter(p => !p.attributes.motion && p.speed > 0).length

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      series: [moving, stopped, idle],
      title: {
        text: t("positionMotion"),
        align: "center",
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 400,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "pie",
        height: 350,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: "bottom",
        fontSize: "16px",
        fontWeight: 600,
        markers: {
          width: 16,
          height: 16,
          radius: 8,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 8,
        },
      },
      labels: [
        t("eventDeviceMoving"),
        t("eventDeviceStopped"),
        t("eventDeviceInactive"),
      ],
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
        },
      },
      colors: ["#4CAF50", "#F44336", "#FFC107"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"],
      },
    }

    const chart = new ApexCharts(ignitionChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [positions, loading, theme, t, textColor, isDark])

  // GRÁFICO DE ESTADOS AGRUPADOS (Última atualização)
  useEffect(() => {
    if (!statesChartRef.current || loading || devices.length === 0) return

    // Inicializar objeto para armazenar estados
    const stateCounts: StateCountsType = {}
    stateCounts["Online"] = []
    stateCounts["12 horas"] = []
    stateCounts["24 horas"] = []
    stateCounts["36 horas"] = []
    stateCounts["48 horas"] = []
    stateCounts["+ 48 horas"] = []

    // Classificar dispositivos por intervalo de tempo
    const now = new Date().getTime()
    devices.forEach((device) => {
      const lastUpdate = new Date(device.lastUpdate).getTime()
      const hoursSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60)

      if (hoursSinceUpdate < 1 && device.status === "online") {
        stateCounts["Online"].push(device.name)
      } else if (hoursSinceUpdate < 12) {
        stateCounts["12 horas"].push(device.name)
      } else if (hoursSinceUpdate < 24) {
        stateCounts["24 horas"].push(device.name)
      } else if (hoursSinceUpdate < 36) {
        stateCounts["36 horas"].push(device.name)
      } else if (hoursSinceUpdate < 48) {
        stateCounts["48 horas"].push(device.name)
      } else {
        stateCounts["+ 48 horas"].push(device.name)
      }
    })

    const stateKeys = Object.keys(stateCounts)
    const stateSeries = stateKeys.map((key) => stateCounts[key].length)

    // Traduzir as chaves de tempo
    const translatedLabels = stateKeys.map((key) => {
      if (key === "Online") return t("deviceStatusOnline")
      if (key === "12 horas") return t("deviceHours12")
      if (key === "24 horas") return t("deviceHours24")
      if (key === "36 horas") return t("deviceHours36")
      if (key === "48 horas") return t("deviceHours48")
      if (key === "+ 48 horas") return t("deviceHoursMore48")
      return key
    })

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      series: stateSeries,
      title: {
        text: t("deviceLastUpdate"),
        align: "center",
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "donut",
        height: 300,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      legend: {
        ...getBaseChartOptions().legend,
        position: isMobile ? "bottom" : "right",
        fontSize: "14px",
        fontWeight: 600,
        markers: {
          width: 14,
          height: 14,
          radius: 7,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      labels: translatedLabels,
      plotOptions: {
        pie: {
          donut: {
            size: "55%",
            background: "transparent",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                color: textColor,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 600,
                color: textColor,
                offsetY: 16,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: t("sharedTotal"),
                fontSize: "22px",
                fontWeight: 700,
                color: textColor,
                formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => Math.round(val) + "%",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          colors: [isDark ? "#fff" : "#000"],
        },
      },
      colors: ["#4CAF50", "#2196F3", "#FFC107", "#FF9800", "#F44336", "#9C27B0"],
      stroke: {
        width: 2,
        colors: [isDark ? "#222" : "#fff"],
      },
    }

    const chart = new ApexCharts(statesChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, loading, theme, t, isMobile, textColor, isDark])

  // GRÁFICO DE ODÔMETRO
  useEffect(() => {
    if (!odometerChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Preparar dados para o gráfico
    const data = positions
      .map((p) => {
        const device = devices.find((d) => d.id === p.deviceId)
        const odometer = p.attributes.odometer ? Math.round(p.attributes.odometer / 1000) : 0
        return {
          deviceName: device ? device.name : "",
          odometer: odometer
        }
      })
      .filter((item) => item.odometer > 0)
      .sort((a, b) => b.odometer - a.odometer)
      .slice(0, 10)

    const chartData = data.map((item) => item.odometer)
    const categoryLabels = data.map((item) => item.deviceName)

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      title: {
        text: t("positionOdometer"),
        align: "center",
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
              },
            },
            chart: {
              height: 250,
            },
          },
        },
      ],
      series: [
        {
          name: t("positionOdometer"),
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: "100%",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          barHeight: "80%",
          distributed: true,
          horizontal: true,
        },
      },
      legend: {
        show: false,
      },
      colors: [
        "#4CAF50", "#2196F3", "#FFC107", "#FF9800", "#F44336",
        "#9C27B0", "#00BCD4", "#795548", "#607D8B", "#3F51B5"
      ],
      grid: {
        show: true,
        borderColor: isDark ? "#333" : "#e0e0e0",
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: ["#fff"],
          fontWeight: "bold",
          fontSize: "14px",
        },
        formatter: (val, opt) => {
          const index = opt.dataPointIndex
          return categoryLabels[index] + ":  " + val + " km"
        },
        offsetX: 10,
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      xaxis: {
        categories: categoryLabels,
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: 600,
            colors: textColor,
          },
        },
        title: {
          text: t("sharedKm"),
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
      },
    }

    const chart = new ApexCharts(odometerChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isDark, textColor])

  // GRÁFICO DE HORAS
  useEffect(() => {
    if (!hoursChartRef.current || loading || positions.length === 0 || devices.length === 0) return

    // Preparar dados para o gráfico
    const data = positions
      .map((p) => {
        const device = devices.find((d) => d.id === p.deviceId)
        const hours = p.attributes.hours ? Math.round(p.attributes.hours / 3600) : 0
        return {
          deviceName: device ? device.name : "",
          hours: hours
        }
      })
      .filter((item) => item.hours > 0)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 10)

    const chartData = data.map((item) => item.hours)
    const categoryLabels = data.map((item) => item.deviceName)

    // Opções do gráfico
    const options = {
      ...getBaseChartOptions(),
      title: {
        text: t("positionHours"),
        align: "center",
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
              },
            },
            chart: {
              height: 250,
            },
          },
        },
      ],
      series: [
        {
          name: t("positionHours"),
          data: chartData,
        },
      ],
      chart: {
        ...getBaseChartOptions().chart,
        type: "bar",
        height: "100%",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          barHeight: "80%",
          distributed: true,
          horizontal: true,
        },
      },
      legend: {
        show: false,
      },
      colors: [
        "#F44336", "#2196F3", "#FFC107", "#4CAF50", "#9C27B0",
        "#FF9800", "#00BCD4", "#795548", "#607D8B", "#3F51B5"
      ],
      grid: {
        show: true,
        borderColor: isDark ? "#333" : "#e0e0e0",
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: ["#fff"],
          fontWeight: "bold",
          fontSize: "14px",
        },
        formatter: (val, opt) => {
          const index = opt.dataPointIndex
          return categoryLabels[index] + ":  " + val + " h"
        },
        offsetX: 10,
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      xaxis: {
        categories: categoryLabels,
        labels: {
          style: {
            fontSize: "14px",
            fontWeight: 600,
            colors: textColor,
          },
        },
        title: {
          text: t("sharedHours"),
          style: {
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          },
        },
      },
    }

    const chart = new ApexCharts(hoursChartRef.current, options)
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [devices, positions, loading, theme, t, isDark, textColor])

  // Exibir indicador de carregamento
  if (loading) {
    return (
      <LoaderContainer>
        <LoaderAnimation>
          <div></div>
        </LoaderAnimation>
      </LoaderContainer>
    )
  }

  // Exibir mensagem de erro
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor={isDark ? "#121212" : "#f5f7fa"}
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          style={{ marginTop: "16px" }}
        >
          {t("sharedRetry")}
        </Button>
      </Box>
    )
  }

  // Renderizar dashboard
  return (
    <Root>
      <Header>
        <Typography variant="h5" component="h1">
          {t("reportDashboard")}
        </Typography>
        <Box display="flex" gap="10px">
          <Button
            variant="outlined"
            color="inherit"
            onClick={colorMode.toggleColorMode}
            startIcon={isDark ? <LightMode /> : <DarkMode />}
            style={{
              borderColor: "rgba(255,255,255,0.3)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            {isDark ? t("themeLightMode") : t("themeDarkMode")}
          </Button>
        </Box>
      </Header>
      <Content>
        {/* Estatísticas rápidas */}
        <Grid container spacing={3} sx={{ marginBottom: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <DirectionsCar sx={{ fontSize: 40, marginBottom: 1 }} />
              <Typography variant="h5">{stats.totalDevices}</Typography>
              <Typography variant="body2">Dispositivos</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Speed sx={{ fontSize: 40, marginBottom: 1 }} />
              <Typography variant="h5">{stats.movingDevices}</Typography>
              <Typography variant="body2">Em Movimento</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <AccessTime sx={{ fontSize: 40, marginBottom: 1 }} />
              <Typography variant="h5">{stats.stoppedDevices}</Typography>
              <Typography variant="body2">Parados</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h5">{stats.totalDistance} km</Typography>
              <Typography variant="body2">Distância Total</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h5">{stats.totalHours} h</Typography>
              <Typography variant="body2">Horas de Motor</Typography>
            </StatCard>
          </Grid>
        </Grid>

        {/* Gráficos */}
        <Grid container spacing={3}>
          {/* Primeira linha: três gráficos de pizza */}
          <Grid item xs={12} md={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom align="center">
                {t("deviceLastUpdate")}
              </Typography>
              <ChartWrapper ref={statesChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom align="center">
                {t("positionMotion")}
              </Typography>
              <ChartWrapper ref={ignitionChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom align="center">
                {t("sharedAlarms")}
              </Typography>
              <ChartWrapper ref={alarmChartRef} />
            </ChartContainer>
          </Grid>

          {/* Segunda linha: gráficos de barras */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom align="center">
                {t("positionOdometer")}
              </Typography>
              <ChartWrapper ref={odometerChartRef} />
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom align="center">
                {t("positionHours")}
              </Typography>
              <ChartWrapper ref={hoursChartRef} />
            </ChartContainer>
          </Grid>
        </Grid>
      </Content>
    </Root>
  )
}

// Componente principal com provedores de tema e tradução
const DashboardWithTheme = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
  const [mode, setMode] = React.useState<"light" | "dark">(prefersDarkMode ? "dark" : "light")

  React.useEffect(() => {
    setMode(prefersDarkMode ? "dark" : "light")
  }, [prefersDarkMode])

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"))
      },
    }),
    [],
  )

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "dark" ? "#90caf9" : "#1976D2",
          },
          secondary: {
            main: mode === "dark" ? "#81c784" : "#4CAF50",
          },
          background: {
            default: mode === "dark" ? "#121212" : "#f5f5f5",
            paper: mode === "dark" ? "#1e1e1e" : "#ffffff",
          },
        },
        typography: {
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        },
      }),
    [mode],
  )

  return (
    <TranslationProvider>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dashboard />
        </ThemeProvider>
      </ColorModeContext.Provider>
    </TranslationProvider>
  )
}

export default DashboardWithTheme